<template>
    <div id="edicaoParticipantesLicitacao">
        <validation-observer ref="validationobserver" v-slot="{ handleSubmit }">
            <b-form @submit.prevent="handleSubmit(salvar)" class="">
                <b-row>
                    <b-col cols="6">
                        <h5 class="text-left">{{ tituloPagina }}</h5>
                    </b-col>
                    <b-col cols="6">
                        <b-button v-if="editParticipante" @click.prevent="limparForm" size="sm" variant="danger"
                            class="float-right">
                            <font-awesome-icon icon="ban" />
                            Cancelar alteração
                        </b-button>
                    </b-col>
                </b-row>
                <b-row class="align-items-stretch">
                    <b-col cols="6" md="2">
                        <default-form-validation-provider label="País" rules="required" v-slot="defState">
                            <b-form-select v-model="novoParticipante.TldParticipante" :state="defState.state"
                                :options="tldPaisesOptions" />
                        </default-form-validation-provider>
                    </b-col>
                    <b-col cols="6" md="3">
                        <default-form-validation-provider label="CPF / CNPJ / Doc."
                            :rules="novoParticipante.TldParticipante === 'BR' ? 'required|min:11|max:14|numeric' : 'required|max:12'"
                            v-slot="defState">
                            <b-form-input v-model="novoParticipante.CdCpfCnpjParticipante" :state="defState.state" />
                        </default-form-validation-provider>
                    </b-col>
                    <b-col cols="6" md="4">
                        <default-form-validation-provider label="Nome / Razão Social" rules="required|max:120"
                            v-slot="defState">
                            <b-form-input v-model="novoParticipante.NmParticipante" :state="defState.state"></b-form-input>
                        </default-form-validation-provider>
                    </b-col>
                    <b-col cols="12" md="3" class="d-flex flex-column justify-content-center">
                        <b-button block type="submit" class="mb-2">
                            <font-awesome-icon :icon="editParticipante ? 'edit' : 'plus'" />
                            {{ editParticipante ? 'Alterar' : 'Adicionar' }}
                        </b-button>
                    </b-col>
                </b-row>
            </b-form>
        </validation-observer>
        <b-row>
            <h5 class="text-left ml-3">{{ tituloTabela }}</h5>
            <b-table :fields="fields" :items="participantes" :busy="tableBusy" :current-page="currentPage" hover
                no-border-collapse sort-by="" :sort-desc="true" stacked="sm" responsive show-empty>
                <template #empty>
                    <span>Não foram encontrados dados.</span>
                </template>
                <template #emptyfiltered>
                    <span>Não foram encontrados dados.</span>
                </template>
                <template #table-busy>
                    <div class="text-center text-dark">
                        <b-spinner class="align-middle"></b-spinner>
                        <strong class="loading-text">Carregando...</strong>
                    </div>
                </template>
                <template v-slot:cell(actions)="row">
                    <div class="actions-icons">
                        <b-link class="text-dark" @click="editarParticipanteLicitacao(row.item)">
                            <font-awesome-icon icon="edit" />
                            <div style="font-size: 12px">Editar</div>
                        </b-link>
                        <btn-excluir :idItem="row.item" :excluirItem="confirmarRemocao" />
                    </div>
                </template>
            </b-table>
            <table-pagination v-if="totalRows > pageSize" :total-rows=totalRows :per-page=pageSize
                @pageChange="pageChange" />
        </b-row>
    </div>
</template>

<script lang="ts">
import TablePagination from "@/components/atos-pessoal/TablePagination.vue"
import DefaultFormValidationProvider from '@/components/shared/DefaultFormValidationProvider.vue'
import BtnExcluir from '@/components/shared/btn-excluir.vue'
import { eventBus } from '@/main'
import ldiService from '@/services/licitacoes/ldiService'
import { store } from "@/store"
import { fieldEdicaoParticipanteLicitacao, fieldsParticipanteLicitacao } from '@/types/licitacoes/ldi/fields-participante-licitacao'
import { ParticipanteLicitacao } from '@/types/licitacoes/ldi/participante-licitacao'
import { TableField } from '@/types/table-field'
import { UtilsTS } from '@/utilsts'
import { ValidationObserver } from "vee-validate"
import { mixins } from 'vue-class-component'
import { Component, Ref, Vue } from 'vue-property-decorator'

const CompProps = Vue.extend({
    name: 'AbaEditarParticipantes',
    components: { BtnExcluir, DefaultFormValidationProvider, TablePagination, ValidationObserver },
    props: {
        lic: {
            type: Object,
            required: true
        },
        mododi: { //modo dispensa/inex. A priori deve apenas trocar o termo participante por credor.
            type: Boolean,
            default: false
        }
    }
})

@Component
export default class AbaEditarParticipantes extends mixins(UtilsTS, CompProps) {
    @Ref('validationobserver') validationobserver: InstanceType<typeof ValidationObserver>
    novoParticipante: ParticipanteLicitacao = new ParticipanteLicitacao()
    editParticipante = false //true para modo edição, false para modo inclusão
    tldPaisesOptions: { value: string; text: string }[] = []

    //table-related
    fields: TableField[] = [...fieldsParticipanteLicitacao, fieldEdicaoParticipanteLicitacao]
    participantes: ParticipanteLicitacao[] = []
    currentPage = 1
    pageSize = 10
    totalRows = 0
    tableBusy = false

    get tituloPagina() {
        return `${this.editParticipante ? 'Atualizar' : 'Adicionar'} ${this.termoSingular}`
    }
    get tituloTabela() {
        return `${this.mododi ? 'Credores' : 'Participantes'} cadastrados`
    }
    get termoSingular() {
        return this.mododi ? 'credor' : 'participante'
    }
    get termoPlural() {
        return this.mododi ? 'credores' : 'participantes'
    }
    get nomeTipo() {
        return this.mododi ? 'dispensa/inexigibilidade' : 'licitação'
    }


    /** Validar e adicionar/alterar um participante a uma licitação existente */
    async salvar(): Promise<void> {
        try {
            store.blockScreen = true
            const isValid = await this.validationobserver.validate();
            if (!isValid) {
                console.error(this.validationobserver)
            } else {
                //salvar
                const retorno = await ldiService.adicionarParticipanteLicitacao(this.novoParticipante)
                if (this.editParticipante) {
                    this.$toast.success(`O ${this.termoSingular} ${retorno.NmParticipante} foi atualizado.`)
                    this.participantes = this.participantes.filter(p => p.IdParticipanteLicitacao !== retorno.IdParticipanteLicitacao)
                } else {
                    this.$toast.success(`O ${this.termoSingular} ${retorno.NmParticipante} foi adicionado a esta ${this.nomeTipo}.`)
                    this.totalRows++
                }
                this.participantes.unshift(retorno)
                eventBus.$emit('atualizar-status-licitacao')
                this.limparForm()
            }
        } catch (e: any) {
            this.tratarError(e)
        } finally {
            store.blockScreen = false
        }
    }
    async limparForm() {
        this.novoParticipante = new ParticipanteLicitacao()
        this.novoParticipante.IdLicitacao = this.lic.IdLicitacao
        this.editParticipante = false
        this.validationobserver.reset();
    }
    async mounted() {
        this.novoParticipante.IdLicitacao = this.lic.IdLicitacao
        this.carregarTldPaises()
        this.loadData()
    }

    async carregarTldPaises() {
        const lista = await ldiService.getListaTldPaises()
        this.tldPaisesOptions = lista.map(p => ({ value: p.Sigla, text: `${p.Sigla} - ${p.Descricao}` }))
    }
    async loadData() {
        try {
            this.tableBusy = true
            const response = await ldiService.buscarParticipantes(this.lic.IdLicitacao, this.currentPage, this.pageSize)
            if (response) {
                this.participantes = response.participantes.map(l => {
                    const participante = Object.assign(new ParticipanteLicitacao(), l)
                    return participante
                })
                //console.log(this.participantes)
                this.totalRows = response.totalRows
            } else {
                this.$toast.error(`Erro ao consultar os ${this.termoPlural}.`);
            }
        } finally {
            this.tableBusy = false;
        }
    }

    async pageChange(value) {
        this.currentPage = value;
        await this.loadData();
    }

    async editarParticipanteLicitacao(value: ParticipanteLicitacao) {
        this.novoParticipante = Object.assign(new ParticipanteLicitacao(), value)
        this.editParticipante = true
    }
    async confirmarRemocao(item: ParticipanteLicitacao) {
        try {
            if (item.IsVencedor) {
                const respostaUsuario = await this.$bvModal.msgBoxConfirm(
                    `Há um ou mais itens nesta ${this.nomeTipo} vencidos por este ${this.termoSingular}. Ao remover o ${this.termoSingular}, os itens perderão seus vencedores e a situação da ${this.nomeTipo} poderá ser alterada.`,
                    {
                        buttonSize: 'sm',
                        cancelTitle: `Não remova este ${this.termoSingular}`,
                        cancelVariant: 'outline-secondary',
                        centered: true,
                        headerBgVariant: 'secondary',
                        headerTextVariant: 'light',
                        okTitle: `Quero remover o ${this.termoSingular}`,
                        title: `Confirmar exclusão de ${this.termoSingular}`,
                    })
                if (respostaUsuario) {
                    await this.removerParticipanteLicitacao(item)
                }
            } else {
                await this.removerParticipanteLicitacao(item)
            }
        } catch (e: any) {
            this.tratarError(e)
        }
    }
    async removerParticipanteLicitacao(item: ParticipanteLicitacao) {
        try {
            const retorno = await ldiService.removerParticipanteLicitacao(item.IdParticipanteLicitacao)
            if (retorno) {
                this.participantes = this.participantes.filter(p => p.IdParticipanteLicitacao !== item.IdParticipanteLicitacao)
                this.$emit('input', this.participantes);
                eventBus.$emit('atualizar-status-licitacao')
                this.$toast.success(`O ${this.termoSingular} ${item.NmParticipante} foi removido desta ${this.nomeTipo}.`)
            }
            else this.$toast.error(`Falha ao remover o ${this.termoSingular} ${item.NmParticipante}.`)
            //console.log('editarParticipantesLicitacao.removerParticipanteLicitacao recebeu', retorno)
        } catch (e: any) {
            this.tratarError(e)
        }
    }

}


</script>

<style scoped>
</style>